/**
 * API client for the PoseRef backend.
 *
 * Authentication uses Bearer tokens (industry standard for SPAs with a
 * separate API domain). The session token is stored in localStorage and
 * sent via the Authorization header on every request.
 */

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? "";
const TOKEN_KEY = "poseref_session_token";

// ---------------------------------------------------------------------------
// Token management
// ---------------------------------------------------------------------------

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredToken(token: string | null): void {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    // localStorage unavailable — token will only persist in memory
  }
}

// ---------------------------------------------------------------------------
// Fetch wrapper
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function apiFetch<T>(
  path: string,
  opts: {
    method?: string;
    body?: unknown;
    signal?: AbortSignal;
  } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body) headers["Content-Type"] = "application/json";

  const token = getStoredToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 401) {
    setStoredToken(null);
    const { useAuthStore } = await import("../app/authStore.ts");
    useAuthStore.getState().handleUnauthorized();
    throw new ApiError(401, "Unauthorized");
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(
      res.status,
      err.message ?? err.error ?? `API error ${res.status}`,
    );
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

/**
 * Upload a blob directly to a presigned URL.
 * Bypasses apiFetch — goes straight to R2, not our API.
 */
export async function uploadToPresignedUrl(
  url: string,
  blob: Blob,
): Promise<void> {
  const res = await fetch(url, {
    method: "PUT",
    body: blob,
    headers: { "Content-Type": blob.type },
  });
  if (!res.ok) {
    throw new ApiError(res.status, `Upload failed: ${res.statusText}`);
  }
}
